import { isEqual, toPath } from 'lodash';
import * as React from 'react';

import {
    ValidationErrorData,
    ValidationResultItem
} from '../../typings/common';

export type FieldPathMap = {
    serverPath: string;
    clientPath: string;
};

const idx = {};

export interface ErrorsMatcher<T extends {}> {
    path: (string | {})[];
    codes: {
        code: string;
        message: (
            error: ValidationResultItem,
            requestParams?: T
        ) => (string | number | object | undefined)[] | string;
    }[];
}

interface ErrorTester {
    pathLength: number;
    testPath: (err: ValidationResultItem) => boolean;
    codes: Record<
        string,
        (
            error: ValidationResultItem,
            requestParams?: {}
        ) => (string | number | object | undefined)[] | string
    >;
}

export type ErrorMapper = (
    error: ValidationResultItem,
    requestParams?: {}
) => React.ReactNode;

export function createErrorsMapper<T>(
    getRules: (opts: { idx: object }) => ErrorsMatcher<T>[],
    getDefaultMessage: (
        error: ValidationResultItem,
        requestParams?: {}
    ) => (string | number | object | undefined)[] | string
): ErrorMapper {
    const list = getRules({ idx: idx })
        .map(function(item: ErrorsMatcher<T>): ErrorTester {
            const itemPath = (item.path || []).slice();

            return {
                pathLength: itemPath.length,
                testPath: (errObj: ValidationResultItem): boolean => {
                    const { path } = errObj,
                        pathArr = toPath(path),
                        templatePathArr = pathArr.map((x: string) =>
                            isFinite(Number(x)) ? idx : x
                        ),
                        startPath = templatePathArr.slice(0, itemPath.length);

                    return isEqual(startPath, itemPath);
                },
                codes: item.codes.reduce((acc, next) => {
                    acc[next.code] = next.message;

                    return acc;
                }, {})
            };
        })
        .sort((a, b) => b.pathLength - a.pathLength);

    return (
        error: ValidationResultItem,
        requestParams?: T
    ): React.ReactNode => {
        for (let i = 0; i < list.length; i++) {
            const item = list[i];

            if (item.testPath(error) && error.code && item.codes[error.code]) {
                return item.codes[error.code](error, requestParams);
            }
        }

        return getDefaultMessage(error, requestParams);
    };
}

export const buildErrorTree = (errors: ValidationResultItem[]): object => {
    return errors.reduce((tree, error) => {
        // недорого прокладываем путь по дереву до листа
        const leaf = toPath(error.path).reduce(
            (node, key) => node[key] || (node[key] = {}),
            tree
        );

        // ошибки всегда должны лежать в массиве в поле errors
        if (!leaf.errors) {
            leaf.errors = [];
        }

        leaf.errors.push(error);

        return tree;
    }, {});
};

export const mapTextToErrors = (
    errors: ValidationResultItem[],
    errorTextMapper: (
        error: ValidationResultItem,
        requestParams?: {}
    ) => React.ReactNode
): ValidationResultItem[] => {
    return errors.map(error => ({
        code: error.code,
        path: error.path,
        text: errorTextMapper({
            code: error.code,
            path: error.path,
            params: error.params
        }),
        params: error.params
    }));
};

export const mapTextToValidationErrorData = (
    errors: ValidationResultItem[],
    errorTextMapper: (
        error: ValidationResultItem,
        requestParams?: {}
    ) => React.ReactNode
): ValidationErrorData[] => {
    return errors.map(error => ({
        code: error.code,
        path: error.path,
        text: errorTextMapper({
            code: error.code,
            path: error.path,
            params: error.params
        })
    }));
};

export const convertServerPathToClient = (
    errors: ValidationResultItem[],
    errorPathMapperBuilder: (index: string) => FieldPathMap[]
): ValidationResultItem[] => {
    return errors.map(error => ({
        ...error,
        path: findClientPath(error.path, errorPathMapperBuilder)
    }));
};

/**
 * Для переданной строки (path ошибки, который пришел с сервера), с помощью функции errorPathMapperBuilder
 * находит соответствующий path который мы будем использовать на клиенте, если не находит, то возвращает `common`
 * пример:
 *     Для: adUpdateItems[1].sitelinkSet.sitelinks[0].title
 *     Используя функцию: bannerFieldErrorMapper
 *     Результат: banners[1].sitelinks[0].title
 *
 * @param errorPath - исходный path
 * @param errorPathMapperBuilder - функция которая возвращает соответствие серверного path к клиентскому
 * @param placeHolder - заменитель для индексов
 */
export const findClientPath = (
    errorPath: string,
    errorPathMapperBuilder: (index: string) => FieldPathMap[],
    placeHolder: string = '_INDEX_'
): string => {
    const errorPathArray = toPath(errorPath);
    const indexes: string[] = [];
    const mapper = errorPathMapperBuilder(placeHolder);
    const serverPathWithoutIndexes = errorPathArray.map((x: string) => {
        if (isFinite(Number(x))) {
            indexes.push(x);
            return placeHolder;
        }
        return x;
    });
    const clientPathArr = mapper.find(mapObg => {
        const errorObjPathArr = toPath(mapObg.serverPath);
        return isEqual(serverPathWithoutIndexes, errorObjPathArr);
    });
    let result: string[] = [];

    if (clientPathArr) {
        let count = 0;
        result = toPath(clientPathArr.clientPath).reduce(
            (acc: string[], next: string): string[] => {
                if (next === placeHolder) {
                    acc.push(indexes[count]);
                    count++;
                } else {
                    acc.push(next);
                }

                return acc;
            },
            []
        );
    } else {
        result = ['common'];
    }

    return result.join('.');
};
